"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { altFor } from "@/lib/portfolio-photos";

export type PortfolioImage = {
  category: string;
  url: string;
};

export type FeaturedProject = {
  slug: string;
  title: string;
  service: string;
  heroImage: string;
  heroAlt: string;
};

const categoryMeta: Record<string, { border: string; accent: string; text: string }> = {
  "Screen Printing":      { border: "border-t-brand-red",   accent: "bg-brand-red",   text: "text-white" },
  "Embroidery":           { border: "border-t-black",        accent: "bg-black",        text: "text-white" },
  "Signs & Banners":      { border: "border-t-amber-400",    accent: "bg-amber-400",    text: "text-black" },
  "Trophies":             { border: "border-t-amber-400",    accent: "bg-amber-400",    text: "text-black" },
  "Trophies & Awards":    { border: "border-t-amber-400",    accent: "bg-amber-400",    text: "text-black" },
  "Vehicles":             { border: "border-t-brand-orange", accent: "bg-brand-orange", text: "text-white" },
  "Decals":               { border: "border-t-brand-orange", accent: "bg-brand-orange", text: "text-white" },
};
const defaultMeta = { border: "border-t-brand-orange", accent: "bg-brand-orange", text: "text-white" };

export default function PortfolioClient({
  images,
  categories,
  projects,
}: {
  images: PortfolioImage[];
  categories: string[];
  projects: FeaturedProject[];
}) {
  const tabs = ["All", ...categories];
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered =
    activeCategory === "All"
      ? images
      : images.filter((img) => img.category === activeCategory);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
  }, [filtered.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % filtered.length));
  }, [filtered.length]);

  // Keyboard controls + body scroll lock while lightbox is open
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const activeImage = lightboxIndex !== null ? filtered[lightboxIndex] : null;

  return (
    <>
      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-brand-red text-[11px] uppercase tracking-[0.45em] font-semibold mb-5">
            Our Work
          </p>
          <h1 className="font-headline text-[clamp(60px,10vw,120px)] text-white uppercase leading-[0.9] mb-6 max-w-4xl">
            Work We&apos;re Proud Of.
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed max-w-2xl">
            A look at completed projects across custom apparel, awards, fleet wraps, and event signage.
          </p>
        </div>
      </section>

      {/* ─── PORTFOLIO NOTICE ────────────────────────────────────────── */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2.5">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" />
          <p className="text-[11px] text-gray-500 tracking-wide">
            We&apos;re actively adding new work, so check back often for the latest projects from our shop.
          </p>
        </div>
      </div>

      {/* ─── FILTER + GRID ────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {tabs.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-14 border-b border-gray-200 pb-6">
              {tabs.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-all duration-150 border ${
                    activeCategory === cat
                      ? "bg-brand-orange text-white border-brand-orange"
                      : "bg-white text-gray-500 border-gray-300 hover:border-brand-orange hover:text-brand-orange"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
              {filtered.map((img, i) => {
                const meta = categoryMeta[img.category] ?? defaultMeta;
                return (
                  <button
                    key={img.url}
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className={`group relative overflow-hidden bg-white border-t-4 ${meta.border} text-left cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange`}
                    aria-label={`Open: ${altFor(img.url, img.category)}`}
                  >
                    <div className="aspect-[4/3] bg-black relative overflow-hidden">
                      <Image
                        src={img.url}
                        alt={altFor(img.url, img.category)}
                        fill
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                        unoptimized
                      />
                      <span
                        className={`absolute top-4 left-4 ${meta.accent} ${meta.text} text-[9px] font-bold uppercase tracking-[0.25em] px-3 py-1.5 z-10`}
                      >
                        {img.category}
                      </span>
                      {/* Hover hint — view icon top-right */}
                      <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-white">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-32">
              <p className="text-gray-400 text-sm uppercase tracking-[0.2em]">
                No projects in this category yet. Check back soon.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ─── FEATURED PROJECTS (compact, secondary to the grid) ──────── */}
      {projects.length > 0 && (
        <section className="bg-gray-50 border-t border-gray-200 py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-brand-red text-[10px] uppercase tracking-[0.35em] font-semibold mb-2">
              Featured Projects
            </p>
            <h2 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-8">
              Project Case Studies
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
              {projects.map((p) => (
                <Link
                  key={p.slug}
                  href={`/portfolio/${p.slug}`}
                  className="group bg-white flex items-center gap-4 p-4"
                >
                  <div className="w-20 h-20 shrink-0 relative overflow-hidden bg-black">
                    <Image
                      src={p.heroImage}
                      alt={p.heroAlt}
                      fill
                      sizes="80px"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      unoptimized
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-brand-orange font-semibold mb-1">
                      {p.service}
                    </p>
                    <h3 className="text-sm font-bold text-gray-900 group-hover:text-brand-orange transition-colors leading-snug">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── LIGHTBOX ─────────────────────────────────────────────────── */}
      {activeImage && lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeImage.category} image`}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-20"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Prev / Next */}
          {filtered.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-20"
                aria-label="Previous image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showNext(); }}
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-20"
                aria-label="Next image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image — stops click propagation so clicking the image doesn't close */}
          <div
            className="relative max-w-[92vw] max-h-[88vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={activeImage.url}
              alt={altFor(activeImage.url, activeImage.category)}
              className="max-w-full max-h-[88vh] object-contain shadow-2xl"
            />
          </div>

          {/* Caption */}
          <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
            <span className="inline-block text-white/70 text-[10px] uppercase tracking-[0.3em] font-semibold">
              {activeImage.category} · {lightboxIndex + 1} / {filtered.length}
            </span>
          </div>
        </div>
      )}

      {/* ─── CTA ──────────────────────────────────────────────────────── */}
      <section className="bg-black py-32 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-brand-red text-[11px] uppercase tracking-[0.45em] font-semibold mb-5">
            Ready to Join Our Work?
          </p>
          <h2 className="font-headline text-[clamp(52px,10vw,120px)] text-white uppercase leading-[0.9] mb-12">
            Let&apos;s Make Something Great.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/order" className="btn-outline-white">
              Start Your Order
            </Link>
            <Link
              href="/contact-us"
              className="inline-flex items-center justify-center border border-white/20 text-white/50 text-[11px] uppercase tracking-[0.25em] font-semibold px-12 py-4 hover:border-white hover:text-white transition-all duration-200"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
